import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { margin, padding, useClassnames } from '@lumapps/classnames';
import { CONTENT_TYPE_ICON_FALLBACK } from '@lumapps/contents/constants';
import { canUserDesignCurrentContent } from '@lumapps/contents/ducks/selectors';
import { CONTENTS } from '@lumapps/contents/keys';
import type { WriteModeContent } from '@lumapps/contents/types';
import { useDataAttributes } from '@lumapps/data-attributes';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import {
    getSelectedDateTimeZone,
    getTimestampFromDateTimeZone,
} from '@lumapps/lumx-date-time-pickers/components/DateTimeZonePickerField/utils';
import { DrawerDateSection } from '@lumapps/lumx-drawer/components/DrawerDateSection';
import { DrawerDivider } from '@lumapps/lumx-drawer/components/DrawerDivider';
import { DrawerGroupsSection } from '@lumapps/lumx-drawer/components/DrawerGroupsSection';
import { DrawerMetadatasSection } from '@lumapps/lumx-drawer/components/DrawerMetadatasSection';
import { DrawerSection } from '@lumapps/lumx-drawer/components/DrawerSection';
import { DrawerTagsSection } from '@lumapps/lumx-drawer/components/DrawerTagsSection';
import { DrawerUserSection } from '@lumapps/lumx-drawer/components/DrawerUserSection';
import { ThumbnailWithIcon } from '@lumapps/lumx-images/components/ThumbnailWithIcon';
import {
    mdiAccountGroupOutline,
    mdiCalendarEnd,
    mdiCalendarStart,
    mdiCheck,
    mdiEyeOffOutline,
    mdiEyeOutline,
    mdiPencil,
    mdiStarOutline,
    mdiTune,
} from '@lumapps/lumx/icons';
import {
    Alignment,
    Button,
    Dialog,
    Emphasis,
    FlexBox,
    Heading,
    Icon,
    Message,
    Orientation,
    Text,
    Toolbar,
    Typography,
} from '@lumapps/lumx/react';
import type { Media } from '@lumapps/medias/types';
import { getImageUrlFromMedia } from '@lumapps/medias/utils';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { connectedUserSelector } from '@lumapps/user/ducks/selectors';
import { getCurrentTimezone } from '@lumapps/utils/timezones/getCurrentTimezone';

import {
    CONTENT_PROPERTY_TO_CONTENT_SETTING_FIELD_NAME,
    CONTENT_PUBLICATION_SOURCE_TYPE,
    WRITER_MODE_SETTINGS,
} from '../../constants';
import { DESIGNER } from '../../keys';
import { ContentPropertiesInSettingsType, ContentPublicationSourceType } from '../../types';
import { legacyDateInputToIsoString } from '../../utils/legacyDateInputToIsoString';

import './index.scss';

export interface ContentSummaryDialogProps {
    isOpen?: boolean;
    onClose: () => void;
    /** Callback on publish or schedule button click */
    onConfirm: () => void;
    onSettingsClick: () => void;
    scope: string;
    /** The targeted publication type (schedule or publish) */
    publicationSourceType: ContentPublicationSourceType;
    /** The content being published */
    currentContent?: WriteModeContent;
}

const CLASSNAME = 'content-summary-dialog';
const DIALOG_TITLE_ID = 'content-summary-title';

/**
 *  Display the current information of a content inside a dialog
 */
export const ContentSummaryDialog: React.FC<ContentSummaryDialogProps> = ({
    isOpen,
    onSettingsClick,
    onClose,
    onConfirm,
    scope,
    currentContent,
    publicationSourceType,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { translateKey, translateObject, translate } = useTranslate();
    const { get } = useDataAttributes(scope);

    const currentUser = useSelector(connectedUserSelector);

    const author = React.useMemo(
        () => currentContent?.authorDetails || currentUser,
        [currentContent?.authorDetails, currentUser],
    );

    const siteId = useSelector(instanceIdSelector);
    const isUserDesigner = useSelector(canUserDesignCurrentContent(currentContent?.customContentType));

    const contentThumbnail = currentContent?.mediaThumbnail
        ? getImageUrlFromMedia(currentContent?.mediaThumbnail as Media, translate)
        : undefined;
    const isSchedulingContent = publicationSourceType === CONTENT_PUBLICATION_SOURCE_TYPE.SCHEDULE;
    const currentTimeZone = getCurrentTimezone();

    const scheduledDate = React.useMemo(() => {
        if (!currentContent?.startDateInput?.date || !isSchedulingContent) {
            return undefined;
        }

        const scheduleDateTimeZone = getSelectedDateTimeZone({
            timestamp: legacyDateInputToIsoString(currentContent?.startDateInput),
            timeZone: currentTimeZone,
        });
        const scheduleDateTimestamp = getTimestampFromDateTimeZone(scheduleDateTimeZone);

        return {
            timestamp: scheduleDateTimestamp,
            timeZone: scheduleDateTimeZone.timeZone.id,
        };
    }, [currentContent?.startDateInput, currentTimeZone, isSchedulingContent]);

    const expirationDate = React.useMemo(() => {
        if (!currentContent?.endDateInput?.date) {
            return undefined;
        }

        const expirationDateTimeZone = getSelectedDateTimeZone({
            timestamp: legacyDateInputToIsoString({
                date: currentContent?.endDateInput.date,
                hour: currentContent?.endDateInput.hour,
                min: currentContent?.endDateInput.min,
            }),
            timeZone: currentTimeZone,
        });
        const expirationDateTimestamp = getTimestampFromDateTimeZone(expirationDateTimeZone);

        return {
            timestamp: expirationDateTimestamp,
            timeZone: expirationDateTimeZone.timeZone.id,
        };
    }, [
        currentContent?.endDateInput?.date,
        currentContent?.endDateInput?.hour,
        currentContent?.endDateInput?.min,
        currentTimeZone,
    ]);

    const featuredStartDate = React.useMemo(() => {
        if (!currentContent?.featuredStartDateInput?.date || !currentContent?.isFeatured) {
            return undefined;
        }

        const featuredStartDateTimeZone = getSelectedDateTimeZone({
            timestamp: legacyDateInputToIsoString(currentContent?.featuredStartDateInput),
            timeZone: currentTimeZone,
        });
        const featuredStartDateTimestamp = getTimestampFromDateTimeZone(featuredStartDateTimeZone);

        return {
            timestamp: featuredStartDateTimestamp,
            timeZone: featuredStartDateTimeZone.timeZone.id,
        };
    }, [currentContent?.featuredStartDateInput, currentContent?.isFeatured, currentTimeZone]);

    const featuredEndDate = React.useMemo(() => {
        if (!currentContent?.featuredEndDateInput?.date || !currentContent?.isFeatured) {
            return undefined;
        }

        const featuredEndDateTimeZone = getSelectedDateTimeZone({
            timestamp: legacyDateInputToIsoString(currentContent?.featuredEndDateInput),
            timeZone: currentTimeZone,
        });
        const featuredEndDateTimestamp = getTimestampFromDateTimeZone(featuredEndDateTimeZone);

        return {
            timestamp: featuredEndDateTimestamp,
            timeZone: featuredEndDateTimeZone.timeZone.id,
        };
    }, [currentContent?.featuredEndDateInput, currentContent?.isFeatured, currentTimeZone]);

    const publicationDate = React.useMemo(() => {
        const publicationDateTimeZone = getSelectedDateTimeZone({
            timestamp: new Date().toISOString(),
            timeZone: currentTimeZone,
        });
        const featuredEndDateTimestamp = getTimestampFromDateTimeZone(publicationDateTimeZone);

        return {
            timestamp: featuredEndDateTimestamp,
            timeZone: publicationDateTimeZone.timeZone.id,
        };
    }, [currentTimeZone]);

    const headerTitle = translateKey(
        isSchedulingContent ? DESIGNER.RECAP_SCHEDULE_TITLE : DESIGNER.RECAP_PUBLISH_TITLE,
    );

    /** In legacy designer metadata is coming from the legacy has a groupedBy object, we need an array. */
    const contentMetadata = React.useMemo(
        () =>
            currentContent?.metadata &&
            Object.values(currentContent?.metadata).flatMap((value) => (Array.isArray(value) ? value : [value])),
        [currentContent?.metadata],
    );

    const contentTags =
        currentContent?.customContentTypeTags &&
        currentContent?.customContentTypeTags?.length > 0 &&
        currentContent?.customContentTypeDetails
            ? currentContent?.customContentTypeDetails.tags.filter(
                  (tag) => currentContent?.customContentTypeTags?.includes(tag.id as string),
              )
            : undefined;

    const shouldDisplayField = React.useCallback(
        (contentPropertyName: keyof ContentPropertiesInSettingsType) => {
            const isValueEmpty = isEmpty(currentContent?.[contentPropertyName]);

            if (isUserDesigner) {
                return !isValueEmpty;
            }

            const relatedFieldName = CONTENT_PROPERTY_TO_CONTENT_SETTING_FIELD_NAME[contentPropertyName];

            if (relatedFieldName) {
                return !isValueEmpty && (WRITER_MODE_SETTINGS as readonly string[]).includes(relatedFieldName);
            }

            return false;
        },
        [currentContent, isUserDesigner],
    );

    const fieldsToDisplay = React.useMemo(
        () => ({
            author,
            writeOnBehalf: shouldDisplayField('writerDetails'),
            publicationDate: !scheduledDate?.timestamp,
            scheduleDate: shouldDisplayField('startDateInput') && scheduledDate?.timestamp,
            expirationDate: shouldDisplayField('endDateInput') && expirationDate?.timestamp,
            visibleBy: shouldDisplayField('feedKeys'),
            editors: shouldDisplayField('editors'),
            featured: currentContent?.isFeatured,
            featuredStartDate: currentContent?.isFeatured && featuredStartDate?.timestamp,
            featuredEndDate: currentContent?.isFeatured && featuredEndDate?.timestamp,
            highlightedFor: shouldDisplayField('featuredFeedKeys'),
            notifiedGroups: shouldDisplayField('notifyFeedKeys'),
            tags: shouldDisplayField('customContentTypeTags') && !isEmpty(contentTags),
            metadata: shouldDisplayField('metadata'),
        }),
        [
            author,
            currentContent?.isFeatured,
            contentTags,
            expirationDate?.timestamp,
            featuredEndDate?.timestamp,
            featuredStartDate?.timestamp,
            scheduledDate?.timestamp,
            shouldDisplayField,
        ],
    );

    const shouldDisplayLastSection =
        !!currentContent?.isFeatured ||
        !!contentMetadata?.length ||
        fieldsToDisplay.featured ||
        fieldsToDisplay.featuredEndDate ||
        fieldsToDisplay.featuredStartDate ||
        fieldsToDisplay.featuredEndDate ||
        fieldsToDisplay.highlightedFor ||
        fieldsToDisplay.notifiedGroups ||
        fieldsToDisplay.tags ||
        fieldsToDisplay.metadata;

    return (
        <Dialog className={block()} isOpen={isOpen} dialogProps={{ 'aria-labelledby': DIALOG_TITLE_ID }}>
            <header className={element('header')}>
                <Toolbar
                    label={
                        <Heading id={DIALOG_TITLE_ID} typography="title" as="h1">
                            {headerTitle}
                        </Heading>
                    }
                />
                <Message
                    className={element('settings-message', [margin('horizontal', 'huge'), margin('bottom', 'huge')])}
                    kind="info"
                    hasBackground
                >
                    <FlexBox
                        className={element('settings-message-content')}
                        orientation={Orientation.horizontal}
                        gap="huge"
                        fillSpace
                    >
                        <Text as="span">
                            {translateKey(
                                isSchedulingContent ? DESIGNER.RECAP_SCHEDULE_MESSAGE : DESIGNER.RECAP_PUBLISH_MESSAGE,
                            )}
                        </Text>
                        <Button emphasis="medium" onClick={onSettingsClick} leftIcon={mdiTune}>
                            {translateKey(GLOBAL.SETTINGS)}
                        </Button>
                    </FlexBox>
                </Message>
            </header>
            <FlexBox className={padding('all', 'huge')} orientation={Orientation.vertical}>
                <FlexBox
                    className={element('recap-sections', [padding('horizontal', 'big')])}
                    orientation="vertical"
                    gap="huge"
                >
                    <FlexBox className={element('title-section')} orientation="horizontal" gap="big" hAlign="center">
                        <ThumbnailWithIcon
                            image={contentThumbnail}
                            icon={currentContent?.customContentTypeDetails?.icon || CONTENT_TYPE_ICON_FALLBACK}
                            isDynamicIcon={!!currentContent?.customContentTypeDetails?.icon}
                            size="l"
                        />
                        <Heading
                            as="h2"
                            typography={Typography.subtitle2}
                            className={element('title')}
                            truncate
                            {...get({ element: 'title' })}
                        >
                            {currentContent?.title
                                ? translateObject(currentContent?.title)
                                : translateKey(GLOBAL.UNTITLED)}
                        </Heading>
                    </FlexBox>
                    {(fieldsToDisplay.author || fieldsToDisplay.writeOnBehalf) && (
                        <div className={element('authoring-section')}>
                            {/* Author section */}
                            {fieldsToDisplay.author && (
                                <FlexBox orientation="vertical" gap="regular">
                                    <DrawerUserSection
                                        title={translateKey(GLOBAL.AUTHOR)}
                                        scope={scope}
                                        user={author}
                                        wrapperProps={{
                                            className: element('author-section', [margin('vertical', null)]),
                                        }}
                                    />
                                    {fieldsToDisplay.writeOnBehalf && (
                                        <FlexBox orientation="horizontal" gap="tiny">
                                            <Icon icon={mdiEyeOffOutline} size="xs" color="dark" colorVariant="L2" />
                                            <Text as="span" typography="body1" color="dark" colorVariant="L2">
                                                {translateKey(GLOBAL.NOT_VISIBLE_TO_READER)}
                                            </Text>
                                        </FlexBox>
                                    )}
                                </FlexBox>
                            )}
                            {/* Writing on behalf of section */}
                            {fieldsToDisplay.writeOnBehalf && (
                                <DrawerUserSection
                                    title={translateKey(CONTENTS.WRITE_ON_BEHALF_OF)}
                                    scope={scope}
                                    user={currentContent?.writerDetails}
                                    wrapperProps={{
                                        className: element('writing-on-behalf-section', [margin('vertical', null)]),
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {(fieldsToDisplay.scheduleDate ||
                        fieldsToDisplay.publicationDate ||
                        fieldsToDisplay.expirationDate) && (
                        <div className={element('schedule-dates-section')}>
                            {/* Schedule date section */}
                            {scheduledDate?.timestamp && fieldsToDisplay.scheduleDate ? (
                                <DrawerDateSection
                                    date={scheduledDate.timestamp}
                                    scope={scope}
                                    title={translateKey(GLOBAL.SCHEDULED_ON)}
                                    format={translateKey(GLOBAL.DATE_FORMAT_COMPLETE)}
                                    icon={mdiCalendarStart}
                                    timeZone={scheduledDate.timeZone}
                                    wrapperProps={{
                                        className: element('schedule-section', [margin('vertical', null)]),
                                    }}
                                />
                            ) : (
                                <DrawerDateSection
                                    date={new Date().toISOString()}
                                    scope={scope}
                                    title={translateKey(GLOBAL.PUBLISHED_ON)}
                                    format={translateKey(GLOBAL.DATE_FORMAT_COMPLETE)}
                                    icon={mdiCalendarStart}
                                    timeZone={publicationDate.timeZone}
                                    wrapperProps={{
                                        className: element('schedule-section', [margin('vertical', null)]),
                                    }}
                                />
                            )}
                            {/* Expire date section */}
                            {expirationDate?.timestamp && fieldsToDisplay.expirationDate && (
                                <DrawerDateSection
                                    date={expirationDate.timestamp}
                                    scope={scope}
                                    title={translateKey(GLOBAL.EXPIRES_ON)}
                                    format={translateKey(GLOBAL.DATE_FORMAT_COMPLETE)}
                                    icon={mdiCalendarEnd}
                                    timeZone={expirationDate.timeZone}
                                    wrapperProps={{
                                        className: element('expire-section', [margin('vertical', null)]),
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {(fieldsToDisplay.visibleBy || fieldsToDisplay.editors) && (
                        <>
                            <DrawerDivider className={margin('vertical', null)} />
                            <FlexBox
                                className={element('groups-section')}
                                orientation="vertical"
                                gap="huge"
                                hAlign="center"
                            >
                                {/* Visible by section */}
                                {fieldsToDisplay.visibleBy && (
                                    <DrawerGroupsSection
                                        title={translateKey(GLOBAL.VISIBLE_BY)}
                                        icon={mdiEyeOutline}
                                        wrapperProps={{
                                            className: element('visible-by-section', [margin('vertical', null)]),
                                        }}
                                        groupsIds={currentContent?.feedKeys}
                                    />
                                )}
                                {/* Editors section */}
                                {fieldsToDisplay.editors && (
                                    <DrawerGroupsSection
                                        title={translateKey(GLOBAL.EDITORS)}
                                        icon={mdiPencil}
                                        wrapperProps={{
                                            className: element('editors-section', [margin('vertical', null)]),
                                        }}
                                        groupsIds={currentContent?.editors}
                                    />
                                )}
                            </FlexBox>
                        </>
                    )}
                    {shouldDisplayLastSection && (
                        <>
                            <DrawerDivider className={margin('vertical', null)} />
                            <FlexBox
                                className={element('metadata-and-other-section')}
                                orientation="vertical"
                                gap="huge"
                                hAlign="center"
                            >
                                {/* Featured content section */}
                                {fieldsToDisplay.featured && (
                                    <DrawerSection
                                        title={translateKey(GLOBAL.FEATURED)}
                                        icon={mdiCheck}
                                        wrapperProps={{
                                            className: element('featured-section', [margin('vertical', null)]),
                                        }}
                                    >
                                        <Text as="span" typography={Typography.body1}>
                                            {translateKey(GLOBAL.YES)}
                                        </Text>
                                    </DrawerSection>
                                )}
                                {(fieldsToDisplay.featuredStartDate || fieldsToDisplay.featuredEndDate) && (
                                    <div className={element('featured-dates-section')}>
                                        {/* Featured start date section */}
                                        {featuredStartDate?.timestamp && fieldsToDisplay.featuredStartDate && (
                                            <DrawerDateSection
                                                date={featuredStartDate.timestamp}
                                                scope={scope}
                                                title={translateKey(DESIGNER.RECAP_FEATURED_START_DATE_LABEL)}
                                                format={translateKey(GLOBAL.DATE_FORMAT_COMPLETE)}
                                                icon={mdiCalendarStart}
                                                timeZone={featuredStartDate.timeZone}
                                                wrapperProps={{
                                                    className: element('featured-start-date-section', [
                                                        margin('vertical', null),
                                                    ]),
                                                }}
                                            />
                                        )}
                                        {/* Featured end date section */}
                                        {featuredEndDate?.timestamp && fieldsToDisplay.featuredEndDate && (
                                            <DrawerDateSection
                                                date={featuredEndDate.timestamp}
                                                scope={scope}
                                                title={translateKey(DESIGNER.RECAP_FEATURED_END_DATE_LABEL)}
                                                format={translateKey(GLOBAL.DATE_FORMAT_COMPLETE)}
                                                icon={mdiCalendarEnd}
                                                timeZone={featuredEndDate.timeZone}
                                                wrapperProps={{
                                                    className: element('featured-end-date-section', [
                                                        margin('vertical', null),
                                                    ]),
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                                {/* Highlighted for section */}
                                {fieldsToDisplay.highlightedFor && (
                                    <DrawerGroupsSection
                                        title={translateKey(GLOBAL.HIGHLIGHTED_FOR)}
                                        icon={mdiStarOutline}
                                        wrapperProps={{
                                            className: element('highlighted-section', [margin('vertical', null)]),
                                        }}
                                        groupsIds={currentContent?.featuredFeedKeys}
                                    />
                                )}
                                {/* Notified groups section */}
                                {fieldsToDisplay.notifiedGroups && (
                                    <DrawerGroupsSection
                                        title={translateKey(GLOBAL.NOTIFY)}
                                        icon={mdiAccountGroupOutline}
                                        wrapperProps={{
                                            className: element('notified-groups-section', [margin('vertical', null)]),
                                        }}
                                        groupsIds={currentContent?.notifyFeedKeys}
                                    />
                                )}
                                {/* Tags section */}
                                {fieldsToDisplay.tags && (
                                    <DrawerTagsSection
                                        title={translateKey(GLOBAL.TAGS)}
                                        wrapperProps={{
                                            className: element('tags-section', [margin('vertical', null)]),
                                        }}
                                        tags={contentTags}
                                    />
                                )}
                                {fieldsToDisplay.metadata && (
                                    <DrawerMetadatasSection
                                        metadataIds={contentMetadata}
                                        instanceId={siteId}
                                        rootOnly={false}
                                        wrapperProps={{
                                            className: element('metadatas-section', [margin('vertical', null)]),
                                        }}
                                    />
                                )}
                            </FlexBox>
                        </>
                    )}
                </FlexBox>
            </FlexBox>
            <footer>
                <FlexBox
                    className={padding('all', 'huge')}
                    orientation={Orientation.horizontal}
                    vAlign={Alignment.right}
                    gap="regular"
                >
                    <Button className={element('cancel-button')} emphasis={Emphasis.medium} onClick={onClose}>
                        {translateKey(GLOBAL.CANCEL)}
                    </Button>
                    <Button
                        className={element('publish-button')}
                        onClick={onConfirm}
                        {...get({ element: 'button', action: 'confirm' })}
                    >
                        {isSchedulingContent ? translateKey(GLOBAL.SCHEDULE) : translateKey(GLOBAL.PUBLISH)}
                    </Button>
                </FlexBox>
            </footer>
        </Dialog>
    );
};
